import {
  Drawer,
  Tabs,
  Spin,
  Divider,
  Space,
  Badge,
  Card,
  List,
  Avatar,
  Select,
  Button,
  Switch,
  Typography,
  message
} from "antd";
import {
  ArrowLeftOutlined,
  HolderOutlined,
  MobileOutlined,
  GlobalOutlined,
  CarOutlined,
  TabletOutlined,
  ShopOutlined
} from "@ant-design/icons";
import { useState, useEffect, useCallback } from "react";
import styles from "../styles/ProductsUI.module.css";

const { Text, Title } = Typography;
const { Option } = Select;

const CHANNEL_METADATA = {
  app: {
    label: "App de Usuario",
    description: "Aplicación móvil para clientes (pedidos a domicilio y pickup).",
    icon: <MobileOutlined />,
  },
  web: {
    label: "Sitio Web",
    description: "Plataforma web de e-commerce y pedidos online.",
    icon: <GlobalOutlined />,
  },
  uber: {
    label: "Uber Eats",
    description: "Integración automática para delivery externo con Uber Eats.",
    icon: <CarOutlined />,
  },
  peya: {
    label: "PedidosYa",
    description: "Integración automática para delivery externo con PedidosYa.",
    icon: <CarOutlined />,
  },
  kiosko: {
    label: "Kiosko",
    description: "Puntos de venta de autoservicio en sucursales físicas.",
    icon: <TabletOutlined />,
  },
};

const getChannelMeta = (name) => {
  const normalized = name.toLowerCase();
  return CHANNEL_METADATA[normalized] || {
    label: name[0].toUpperCase() + name.slice(1),
    description: "Canal de venta externo o de terceros.",
    icon: <ShopOutlined />,
  };
};

const ProductDrawer = (props) => {
  const {
    visible,
    onClose,
    selectedRecord,
    user,
    products,
    confirmVisibility,
    getProductMenu,
    getProductSteps,
    updateProductStepsOrder
  } = props;

  // Drawer Local State
  const [activeTab, setActiveTab] = useState("1");
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [productDetails, setProductDetails] = useState(null);
  const [drawerHistory, setDrawerHistory] = useState([]);

  const [stepsLoading, setStepsLoading] = useState(false);
  const [channelsList, setChannelsList] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [productSteps, setProductSteps] = useState([]);
  const [rawStepsData, setRawStepsData] = useState([]);
  const [customOrdersData, setCustomOrdersData] = useState([]);
  const [localRecord, setLocalRecord] = useState(null);

  // Drag and Drop reordering states
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedOverIndex, setDraggedOverIndex] = useState(null);

  const currentProduct = localRecord ? products.find((p) => p.sku === localRecord.sku) : null;

  const loadDrawerData = useCallback(async (record, tabKey = "1", isInitial = false) => {
    setActiveTab(tabKey);
    if (isInitial) {
      setDrawerHistory([]);
    }
    
    setDetailsLoading(true);
    setProductDetails(null);
    
    if (user.admin === 1) {
      setStepsLoading(true);
      setProductSteps([]);
      setRawStepsData([]);
      setCustomOrdersData([]);
    }

    try {
      const detailsPromise = getProductMenu(record.sku, user.branch);
      let stepsPromise = Promise.resolve(null);
      
      if (user.admin === 1) {
        stepsPromise = getProductSteps(record.sku);
      }

      const [detailsRes, stepsRes] = await Promise.all([detailsPromise, stepsPromise]);

      if (detailsRes.data && detailsRes.data.status === "success" && detailsRes.data.product) {
        setProductDetails(detailsRes.data.product);
      } else {
        message.error("No se pudo cargar la receta del producto");
      }

      if (stepsRes && stepsRes.data && stepsRes.data.status === "success") {
        setChannelsList(stepsRes.data.channels || []);
        setRawStepsData(stepsRes.data.steps || []);
        setCustomOrdersData(stepsRes.data.customOrders || []);
        
        const defaultChannel = stepsRes.data.channels && stepsRes.data.channels.length > 0 ? stepsRes.data.channels[0].id : null;
        setSelectedChannel(defaultChannel);
        
        if (defaultChannel) {
          loadStepsForChannel(stepsRes.data.steps || [], stepsRes.data.customOrders || [], defaultChannel);
        }
      }
    } catch (e) {
      message.error("Error al cargar la información del producto");
    } finally {
      setDetailsLoading(false);
      setStepsLoading(false);
    }
  }, [getProductMenu, getProductSteps, user.admin, user.branch]);

  // Initial Data Loading when Selected Record changes
  useEffect(() => {
    if (visible && selectedRecord) {
      setLocalRecord(selectedRecord);
      loadDrawerData(selectedRecord, "1", true);
    }
  }, [visible, selectedRecord, loadDrawerData]);

  const loadStepsForChannel = (rawSteps, customOrders, channelId) => {
    const steps = [...rawSteps];
    steps.forEach(step => {
      const custom = customOrders.find(o => o.step_type === step.type && Number(o.step_id) === Number(step.id) && Number(o.channel_id) === Number(channelId));
      step.setOrder = custom ? custom.setOrder : step.defaultOrder;
    });
    steps.sort((a, b) => a.setOrder - b.setOrder);
    setProductSteps(steps);
  };

  const handleChannelChange = (val) => {
    setSelectedChannel(val);
    loadStepsForChannel(rawStepsData, customOrdersData, val);
  };

  const handleDragDropIndex = (draggedIdx, targetIdx) => {
    if (draggedIdx === null || targetIdx === null || draggedIdx === targetIdx) return;
    const steps = [...productSteps];
    const [draggedItem] = steps.splice(draggedIdx, 1);
    steps.splice(targetIdx, 0, draggedItem);
    
    // Recalcular el orden
    steps.forEach((s, idx) => {
      s.setOrder = idx;
    });
    setProductSteps(steps);
  };

  const handleSaveStepsOrder = async () => {
    if (!localRecord || !selectedChannel) return;
    setStepsLoading(true);
    try {
      const orderList = productSteps.map(s => ({
        type: s.type,
        id: s.id,
        setOrder: s.setOrder
      }));
      const res = await updateProductStepsOrder(localRecord.sku, selectedChannel, orderList, user.id);
      if (res.data && res.data.status === "success") {
        message.success("Orden de pasos actualizado con éxito");
        onClose();
      } else {
        message.error("Error al guardar el orden de pasos");
      }
    } catch (e) {
      message.error("Error al guardar");
    } finally {
      setStepsLoading(false);
    }
  };

  const navigateToSubProduct = async (subItemSku, subItemName) => {
    setDrawerHistory(prev => [
      ...prev,
      {
        localRecord,
        productDetails,
        activeTab,
        channelsList,
        selectedChannel,
        productSteps,
        rawStepsData,
        customOrdersData
      }
    ]);

    const nextRecord = { sku: subItemSku, name: subItemName };
    setLocalRecord(nextRecord);
    await loadDrawerData(nextRecord, "1", false);
  };

  const handleBackDrawer = () => {
    if (drawerHistory.length === 0) return;
    const prev = drawerHistory[drawerHistory.length - 1];
    setDrawerHistory(prevHistory => prevHistory.slice(0, -1));

    setLocalRecord(prev.localRecord);
    setProductDetails(prev.productDetails);
    setActiveTab(prev.activeTab);
    setChannelsList(prev.channelsList);
    setSelectedChannel(prev.selectedChannel);
    setProductSteps(prev.productSteps);
    setRawStepsData(prev.rawStepsData);
    setCustomOrdersData(prev.customOrdersData);
  };

  return (
    <Drawer
      title={
        <Space>
          {drawerHistory.length > 0 && (
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBackDrawer}
              size="small"
            >
              Atrás
            </Button>
          )}
          <span>{`Gestión de Producto - SKU: ${localRecord?.sku || ''}`}</span>
        </Space>
      }
      placement="right"
      width={700}
      onClose={() => {
        onClose();
        setProductDetails(null);
        setLocalRecord(null);
        setDrawerHistory([]);
      }}
      visible={visible}
      destroyOnClose
    >
      <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key)}>
        <Tabs.TabPane tab="Receta y Condimentos" key="1">
          {detailsLoading ? (
            <div className={styles.spinCenterContainer}>
              <Spin size="large" />
            </div>
          ) : productDetails ? (
            <div>
              <div className={styles.detailsHeader}>
                {productDetails.photo && (
                  <img
                    src={productDetails.photo}
                    alt={productDetails.name}
                    className={styles.detailsImg}
                  />
                )}
                <div>
                  <Title level={4} className={styles.titleNoMargin}>{productDetails.name}</Title>
                  <Text type="secondary">SKU: {productDetails.id}</Text>
                  <br />
                  <Text strong className={styles.detailsPrice}>Precio Base: ${productDetails.price?.toFixed(2)}</Text>
                  {productDetails.description && (
                    <p className={styles.detailsDesc}>{productDetails.description}</p>
                  )}
                </div>
              </div>

              <Divider className={styles.dividerMargin15_0} />

              <Title level={5}>Pasos / Condimentos Configurados</Title>
              {productDetails.categories && productDetails.categories.length > 0 ? (
                <div className={styles.stepsListContainer}>
                  {productDetails.categories.map((step, idx) => (
                    <Card
                      key={step.id || idx}
                      title={
                        <div className={styles.stepCardTitle}>
                          <span className={styles.stepTitleText}>Paso {idx + 1}: {step.name}</span>
                          <Space>
                            <Badge status={step.required === 'Si' ? 'processing' : 'default'} text={step.required === 'Si' ? 'Requerido' : 'Opcional'} />
                            <span className={styles.stepCardTag}>Mín: {step.min} / Máx: {step.max}</span>
                          </Space>
                        </div>
                      }
                      size="small"
                      className={styles.stepCard}
                    >
                      <List
                        grid={{ gutter: 12, column: 2 }}
                        dataSource={step.extras || []}
                        renderItem={item => {
                          const targetProd = products.find(p => String(p.sku) === String(item.id));
                          const isSubProduct = targetProd && targetProd.has_steps === 1;
                          return (
                            <List.Item className={styles.extraListItem}>
                              <Card size="small" className={styles.extraCard}>
                                <div className={styles.extraFlexWrapper}>
                                  {item.image && (
                                    <Avatar src={item.image} shape="square" size={32} />
                                  )}
                                  <div className={styles.extraTextWrapper}>
                                    <Text strong className={styles.extraTitle} ellipsis={{ tooltip: item.name }}>{item.name}</Text>
                                    <Text type="secondary" className={styles.extraSku}>
                                      SKU: {item.id}
                                    </Text>
                                    {isSubProduct && (
                                      <Button
                                        type="link"
                                        className={styles.subProductBtn}
                                        onClick={() => navigateToSubProduct(item.id, item.name)}
                                      >
                                        Ver condimentos/pasos
                                      </Button>
                                    )}
                                  </div>
                                  <Text type="success" strong className={styles.extraPrice}>
                                    {item.price > 0 ? `+$${item.price.toFixed(2)}` : 'Gratis'}
                                  </Text>
                                </div>
                              </Card>
                            </List.Item>
                          );
                        }}
                      />
                    </Card>
                  ))}
                </div>
              ) : (
                <Text type="secondary">Este producto no cuenta con condimentos o combos configurados.</Text>
              )}
            </div>
          ) : (
            <div className={styles.recipeNotFoundContainer}>
              <Text type="secondary">No se encontró información de receta.</Text>
            </div>
          )}
        </Tabs.TabPane>

        {user.admin === 1 && (
          <Tabs.TabPane tab="Disponibilidad por Canal" key="2">
            <div className={styles.drawerTabContainer}>
              <Text type="secondary" className={styles.stepsSortHelpText}>
                Activa o desactiva la disponibilidad de este producto en los siguientes canales a nivel central:
              </Text>
              <Divider className={styles.dividerMargin10_0} style={{ margin: "8px 0 16px 0" }} />
              <div className={styles.channelCardList}>
                {currentProduct?.channels?.map((channel) => {
                  const meta = getChannelMeta(channel.name);
                  return (
                    <div
                      key={channel.name}
                      className={`${styles.channelCard} ${channel.active ? styles.channelCardActive : ""}`}
                    >
                      <div className={styles.channelCardContent}>
                        <div className={styles.channelHeader}>
                          <div className={styles.channelAvatar}>
                            {meta.icon}
                          </div>
                          <div className={styles.channelInfo}>
                            <h4 className={styles.channelTitle}>{meta.label}</h4>
                            <span className={styles.channelDescription}>{meta.description}</span>
                          </div>
                        </div>
                        <Switch
                          checkedChildren="Si"
                          unCheckedChildren="No"
                          checked={channel.active}
                          onChange={(checked) => confirmVisibility(currentProduct?.sku, channel.name, !checked)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Tabs.TabPane>
        )}

        {user.admin === 1 && (
          <Tabs.TabPane tab="Ordenar Pasos" key="3">
            <div className={styles.drawerTabContainer}>
              <div className={styles.rowBetweenCenter}>
                <Text strong>Canal:</Text>
                <Select
                  className={styles.channelSelect}
                  value={selectedChannel}
                  onChange={handleChannelChange}
                >
                  {channelsList.map(ch => (
                    <Option key={ch.id} value={ch.id}>
                      {ch.name.toUpperCase()}
                    </Option>
                  ))}
                </Select>
              </div>

              <Divider className={styles.dividerMargin10_0} />

              <Text type="secondary" className={styles.stepsSortHelpText}>
                Modifica el orden de los pasos/grupos de condimento para este canal utilizando los botones de subir/bajar:
              </Text>

              {stepsLoading && productSteps.length === 0 ? (
                <div className={styles.spinCenterContainerMedium}>
                  <Spin />
                </div>
              ) : productSteps.length > 0 ? (
                <>
                  <List
                    bordered
                    dataSource={productSteps}
                    renderItem={(step, index) => (
                      <List.Item
                        draggable
                        className={`${draggedIndex === index ? styles.rowDragging : ""} ${draggedOverIndex === index ? styles.rowDragOver : ""}`}
                        style={{ cursor: "grab" }}
                        onDragStart={(event) => {
                          setDraggedIndex(index);
                          event.dataTransfer.setData("draggedIndex", String(index));
                          event.dataTransfer.effectAllowed = "move";
                        }}
                        onDragOver={(event) => {
                          event.preventDefault();
                          if (draggedOverIndex !== index) {
                            setDraggedOverIndex(index);
                          }
                        }}
                        onDragLeave={() => {
                          if (draggedOverIndex === index) {
                            setDraggedOverIndex(null);
                          }
                        }}
                        onDragEnd={() => {
                          setDraggedIndex(null);
                          setDraggedOverIndex(null);
                        }}
                        onDrop={(event) => {
                          const draggedIdx = Number(event.dataTransfer.getData("draggedIndex"));
                          const targetIdx = index;
                          setDraggedIndex(null);
                          setDraggedOverIndex(null);
                          if (!isNaN(draggedIdx) && draggedIdx !== targetIdx) {
                            handleDragDropIndex(draggedIdx, targetIdx);
                          }
                        }}
                      >
                        <div className={styles.dragger}>
                          <HolderOutlined />
                        </div>
                        <List.Item.Meta
                          title={`Paso ${index + 1}: ${step.name}`}
                          description={
                            <span className={styles.stepTypeTag}>
                              Tipo: {step.type === 'setmenu' ? 'Set Menu / Combo' : 'Grupo Condimentos'} (ID: {step.id})
                            </span>
                          }
                        />
                      </List.Item>
                    )}
                  />
                  <div className={styles.saveBtnRow}>
                    <Button type="primary" loading={stepsLoading} onClick={handleSaveStepsOrder}>
                      Guardar Orden
                    </Button>
                  </div>
                </>
              ) : (
                <Text type="secondary" className={styles.stepsEmptyContainer}>
                  Este producto no tiene pasos configurados.
                </Text>
              )}
            </div>
          </Tabs.TabPane>
        )}
      </Tabs>
    </Drawer>
  );
};

export default ProductDrawer;
